import React from 'react';
import {
  ClipboardList,
  Check,
  AlertTriangle,
  Users,
  Link2,
  LayoutDashboard,
  Lock,
  Trophy,
  Circle
} from 'lucide-react';
import { PREMIUM_COLORS } from './constants';

// Custom golf ball icon component
const GolfBallIcon: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
  >
    <circle cx="12" cy="12" r="10" />
    {/* Golf ball dimples */}
    <circle cx="8" cy="9" r="1.5" fill="currentColor" opacity="0.3" />
    <circle cx="14" cy="8" r="1.5" fill="currentColor" opacity="0.3" />
    <circle cx="16" cy="13" r="1.5" fill="currentColor" opacity="0.3" />
    <circle cx="10" cy="14" r="1.5" fill="currentColor" opacity="0.3" />
    <circle cx="12" cy="11" r="1.5" fill="currentColor" opacity="0.3" />
  </svg>
);

// Custom spinner component for generic loading
const SpinnerIcon: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => (
  <div
    className={className}
    style={{
      ...style,
      borderRadius: '50%',
      border: '3px solid rgba(82, 183, 136, 0.2)',
      borderTopColor: style?.color || PREMIUM_COLORS.accentBrightGreen,
      animation: 'spin 1s linear infinite',
    }}
  />
);

type LoadingVariant =
  | 'scorecard'      // Round completion - scanning scorecard
  | 'lobby'          // Multiplayer lobby loading
  | 'join-game'      // Joining a multiplayer game
  | 'dashboard'      // Dashboard loading
  | 'start-round'    // Starting a new round
  | 'auth'           // Login/signup
  | 'leaderboard'    // Loading leaderboard
  | 'generic';       // Generic loading

type AnimationType = 'scanning' | 'success' | 'error';

interface PremiumLoadingScreenProps {
  variant: LoadingVariant;
  type?: AnimationType;
  size?: 'sm' | 'md' | 'lg';
  title?: string;
  message?: string;
  progress?: number; // 0-100
  showProgress?: boolean;
}

const VARIANT_CONFIG: Record<LoadingVariant, {
  icon: React.ElementType;
  defaultTitle: string;
  defaultMessage: string;
  useSpinnerWithIcon?: boolean; // Spinning circle with icon inside
}> = {
  scorecard: {
    icon: ClipboardList,
    defaultTitle: 'Scanning Your Scorecard',
    defaultMessage: 'Analyzing your round performance...',
  },
  lobby: {
    icon: Users,
    defaultTitle: 'Setting Up the Game',
    defaultMessage: 'Preparing the lobby for players...',
  },
  'join-game': {
    icon: Link2,
    defaultTitle: 'Connecting to Game',
    defaultMessage: 'Finding your game session...',
  },
  dashboard: {
    icon: LayoutDashboard,
    defaultTitle: 'Loading Dashboard',
    defaultMessage: 'Fetching your latest stats...',
  },
  'start-round': {
    icon: ClipboardList, // Scorecard icon inside spinning circle
    defaultTitle: 'Preparing Your Round',
    defaultMessage: 'Setting up the course...',
    useSpinnerWithIcon: true, // Special flag for spinner + icon combo
  },
  auth: {
    icon: Lock,
    defaultTitle: 'Signing You In',
    defaultMessage: 'Verifying your credentials...',
  },
  leaderboard: {
    icon: Trophy,
    defaultTitle: 'Loading Leaderboard',
    defaultMessage: 'Fetching latest standings...',
  },
  generic: {
    icon: SpinnerIcon,
    defaultTitle: 'Loading',
    defaultMessage: 'Please wait...',
  },
};

export function PremiumLoadingScreen({
  variant,
  type = 'scanning',
  size = 'lg',
  title,
  message,
  progress,
  showProgress = false,
}: PremiumLoadingScreenProps) {
  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;
  const displayTitle = title || config.defaultTitle;
  const displayMessage = message || config.defaultMessage;

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return { container: 'w-16 h-16', icon: 'h-6 w-6' };
      case 'lg': return { container: 'w-24 h-24', icon: 'h-10 w-10' };
      default: return { container: 'w-20 h-20', icon: 'h-8 w-8' };
    }
  };

  const sizeClasses = getSizeClasses();

  const renderAnimation = () => {
    if (type === 'success') {
      return (
        <div className="flex items-center justify-center">
          <div className={`${sizeClasses.container} relative`}>
            <div
              className="absolute inset-0 rounded-full animate-ping"
              style={{
                backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}20`,
                animationDuration: '1s',
                animationIterationCount: '2'
              }}
            />
            <div
              className={`${sizeClasses.container} rounded-full flex items-center justify-center`}
              style={{ backgroundColor: PREMIUM_COLORS.accentBrightGreen }}
            >
              <Check className={`${sizeClasses.icon} text-white`} />
            </div>
          </div>
        </div>
      );
    }

    if (type === 'error') {
      return (
        <div className="flex items-center justify-center">
          <div className={`${sizeClasses.container} relative`}>
            <div
              className={`${sizeClasses.container} rounded-full flex items-center justify-center`}
              style={{ backgroundColor: PREMIUM_COLORS.errorRed }}
            >
              <AlertTriangle className={`${sizeClasses.icon} text-white`} />
            </div>
          </div>
        </div>
      );
    }

    // Generic variant - simple spinning loader (no icon)
    if (variant === 'generic') {
      return (
        <div className="flex items-center justify-center">
          <div className={`${sizeClasses.container} relative`}>
            {/* Outer glow */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}10`,
              }}
            />
            {/* Spinning circle */}
            <div
              className={`${sizeClasses.container} rounded-full`}
              style={{
                border: '4px solid rgba(82, 183, 136, 0.2)',
                borderTopColor: PREMIUM_COLORS.accentBrightGreen,
                animation: 'spin 1s linear infinite',
              }}
            />
          </div>
        </div>
      );
    }

    // Spinner with icon inside (e.g., start-round)
    if (config.useSpinnerWithIcon) {
      return (
        <div className="flex items-center justify-center">
          <div className={`${sizeClasses.container} relative`}>
            {/* Background circle */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}10`,
              }}
            />
            {/* Spinning border */}
            <div
              className={`absolute inset-0 rounded-full`}
              style={{
                border: '4px solid rgba(82, 183, 136, 0.2)',
                borderTopColor: PREMIUM_COLORS.accentBrightGreen,
                animation: 'spin 1s linear infinite',
              }}
            />
            {/* Icon in center */}
            <div className={`${sizeClasses.container} rounded-full flex items-center justify-center`}>
              <Icon
                className={`${sizeClasses.icon}`}
                style={{ color: PREMIUM_COLORS.accentBrightGreen }}
              />
            </div>
          </div>
        </div>
      );
    }

    // Scanning animation (for all other variants)
    return (
      <div className="flex items-center justify-center">
        <div className={`${sizeClasses.container} relative`}>
          {/* Pulsing effect */}
          <div
            className="absolute inset-0 rounded-2xl animate-ping"
            style={{
              backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}20`,
              animationDuration: '2s'
            }}
          />

          {/* Icon container */}
          <div
            className={`${sizeClasses.container} rounded-2xl flex items-center justify-center relative z-10`}
            style={{ backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}15` }}
          >
            <Icon
              className={`${sizeClasses.icon}`}
              style={{ color: PREMIUM_COLORS.accentBrightGreen }}
            />
          </div>

          {/* Scanning lines */}
          <div className="absolute inset-0 overflow-hidden rounded-2xl">
            <div
              className="absolute left-0 right-0 h-0.5"
              style={{
                background: `linear-gradient(90deg, transparent, ${PREMIUM_COLORS.accentBrightGreen}, transparent)`,
                animation: 'scan-line 1.5s ease-in-out infinite',
                top: '30%'
              }}
            />
            <div
              className="absolute left-0 right-0 h-0.5"
              style={{
                background: `linear-gradient(90deg, transparent, ${PREMIUM_COLORS.accentBrightGreen}, transparent)`,
                animation: 'scan-line 1.5s ease-in-out infinite 0.5s',
                top: '70%'
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-app flex items-center justify-center p-4">
      <div
        className="w-full max-w-sm p-8 text-center"
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(27, 67, 50, 0.1)',
        }}
      >
        {/* Animation */}
        <div className="mb-6">
          {renderAnimation()}
        </div>

        {/* Title */}
        <h3
          className="text-lg font-semibold mb-2"
          style={{ color: PREMIUM_COLORS.primaryGolfGreen }}
        >
          {type === 'success' ? 'Complete!' : type === 'error' ? 'Something went wrong' : displayTitle}
        </h3>

        {/* Message */}
        <p className="text-sm text-gray-500 mb-6">
          {displayMessage}
        </p>

        {/* Progress Bar */}
        {showProgress && type === 'scanning' && progress !== undefined && (
          <div className="space-y-2">
            <div
              className="w-full h-2 rounded-full overflow-hidden"
              style={{ backgroundColor: 'rgba(82, 183, 136, 0.2)' }}
            >
              <div
                className="h-full rounded-full transition-all duration-300 ease-out"
                style={{
                  width: `${progress}%`,
                  background: PREMIUM_COLORS.heroGradient
                }}
              />
            </div>
            <p className="text-xs" style={{ color: PREMIUM_COLORS.accentBrightGreen }}>
              {progress}% complete
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Standalone animation component (for embedding in existing layouts)
export function PremiumLoadingAnimation({
  variant,
  type = 'scanning',
  size = 'md',
}: {
  variant: LoadingVariant;
  type?: AnimationType;
  size?: 'sm' | 'md' | 'lg';
}) {
  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return { container: 'w-12 h-12', icon: 'h-5 w-5' };
      case 'lg': return { container: 'w-20 h-20', icon: 'h-8 w-8' };
      default: return { container: 'w-16 h-16', icon: 'h-6 w-6' };
    }
  };

  const sizeClasses = getSizeClasses();

  if (type === 'success') {
    return (
      <div className="flex items-center justify-center">
        <div className={`${sizeClasses.container} relative`}>
          <div
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}20`,
              animationDuration: '1s',
              animationIterationCount: '2'
            }}
          />
          <div
            className={`${sizeClasses.container} rounded-full flex items-center justify-center`}
            style={{ backgroundColor: PREMIUM_COLORS.accentBrightGreen }}
          >
            <Check className={`${sizeClasses.icon} text-white`} />
          </div>
        </div>
      </div>
    );
  }

  if (type === 'error') {
    return (
      <div className="flex items-center justify-center">
        <div className={`${sizeClasses.container} relative`}>
          <div
            className={`${sizeClasses.container} rounded-full flex items-center justify-center`}
            style={{ backgroundColor: PREMIUM_COLORS.errorRed }}
          >
            <AlertTriangle className={`${sizeClasses.icon} text-white`} />
          </div>
        </div>
      </div>
    );
  }

  // Generic variant - simple spinning loader (no icon)
  if (variant === 'generic') {
    return (
      <div className="flex items-center justify-center">
        <div className={`${sizeClasses.container} relative`}>
          <div
            className="absolute inset-0 rounded-full"
            style={{
              backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}10`,
            }}
          />
          <div
            className={`${sizeClasses.container} rounded-full`}
            style={{
              border: '3px solid rgba(82, 183, 136, 0.2)',
              borderTopColor: PREMIUM_COLORS.accentBrightGreen,
              animation: 'spin 1s linear infinite',
            }}
          />
        </div>
      </div>
    );
  }

  // Spinner with icon inside (e.g., start-round)
  if (config.useSpinnerWithIcon) {
    return (
      <div className="flex items-center justify-center">
        <div className={`${sizeClasses.container} relative`}>
          <div
            className="absolute inset-0 rounded-full"
            style={{
              backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}10`,
            }}
          />
          <div
            className={`absolute inset-0 rounded-full`}
            style={{
              border: '3px solid rgba(82, 183, 136, 0.2)',
              borderTopColor: PREMIUM_COLORS.accentBrightGreen,
              animation: 'spin 1s linear infinite',
            }}
          />
          <div className={`${sizeClasses.container} rounded-full flex items-center justify-center`}>
            <Icon
              className={`${sizeClasses.icon}`}
              style={{ color: PREMIUM_COLORS.accentBrightGreen }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Scanning animation for other variants
  return (
    <div className="flex items-center justify-center">
      <div className={`${sizeClasses.container} relative`}>
        <div
          className="absolute inset-0 rounded-2xl animate-ping"
          style={{
            backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}20`,
            animationDuration: '2s'
          }}
        />
        <div
          className={`${sizeClasses.container} rounded-2xl flex items-center justify-center relative z-10`}
          style={{ backgroundColor: `${PREMIUM_COLORS.accentBrightGreen}15` }}
        >
          <Icon
            className={`${sizeClasses.icon}`}
            style={{ color: PREMIUM_COLORS.accentBrightGreen }}
          />
        </div>
        <div className="absolute inset-0 overflow-hidden rounded-2xl">
          <div
            className="absolute left-0 right-0 h-0.5"
            style={{
              background: `linear-gradient(90deg, transparent, ${PREMIUM_COLORS.accentBrightGreen}, transparent)`,
              animation: 'scan-line 1.5s ease-in-out infinite',
              top: '30%'
            }}
          />
          <div
            className="absolute left-0 right-0 h-0.5"
            style={{
              background: `linear-gradient(90deg, transparent, ${PREMIUM_COLORS.accentBrightGreen}, transparent)`,
              animation: 'scan-line 1.5s ease-in-out infinite 0.5s',
              top: '70%'
            }}
          />
        </div>
      </div>
    </div>
  );
}
